import { i18n } from '../locales/locales'
import colorsea from 'colorsea'
import { report } from '@/classes/manager/ExceptionReportingManager'

class Task {
  task_id: number
  name: string
  nameIsCustom: boolean
  constructor(id: number, name: string, nameIsCustom: boolean) {
    this.task_id = id
    this.name = name
    this.nameIsCustom = nameIsCustom
  }

  getName() {
    const { t } = i18n
    if (this.nameIsCustom) {
      return this.name
    }
    const technicalName = this.getTechnicalName()
    if (technicalName.length === 0) {
      report(
        new Error(
          'Task name not found for task: (id, name, nameIsCustom) :' +
            this.task_id +
            ', ' +
            this.name +
            ', ' +
            this.nameIsCustom,
        ),
      )
      return ''
    }
    return t('words.' + technicalName)
  }

  static getTechnicalName(id: number): string {
    switch (id) {
      case 10:
        return 'work_time'
      case 5:
        return 'break'
      case 2:
        return 'loading_time'
      case 3:
        return 'driving_time'
      case 9:
        return 'business_trip'
      case 4:
        return 'project'
      case 6:
        return 'special_one'
      case 7:
        return 'special_two'
      case 11:
        return 'not_rated'
      case 12:
        return 'logout_during_core_time'
      case 13:
        return 'absent_during_core_time'
      default:
        return ''
    }
  }

  static getTechnicalNameFinish(id: number): string {
    const { t } = i18n
    switch (id) {
      case 10:
        return t('phrases.finish_work_time')
      case 5:
        return t('phrases.finish_break')
      case 2:
        return t('phrases.finish_loading_time')
      case 3:
        return t('phrases.finish_driving_time')
      case 9:
        return t('phrases.finish_business_trip')
      case 4:
        return t('phrases.finish_project_as_task')
      case 6:
        return t('phrases.finish_special_one')
      case 7:
        return t('phrases.finish_special_two')
      case 8:
        return t('words.clockin')
      default:
        return ''
    }
  }

  getTechnicalName(): string {
    if (this.nameIsCustom) {
      return this.name
    }
    return Task.getTechnicalName(this.task_id)
  }

  static getSentence(id: number, employeeCount: number): string {
    const { t } = i18n
    let key = ''
    switch (id) {
      case 10:
        key = 'sentence_work_time'
        break
      case 5:
        key = 'sentence_break'
        break
      case 2:
        key = 'sentence_loading_time'
        break
      case 3:
        key = 'sentence_driving_time'
        break
      case 6:
        key = 'sentence_special_one'
        break
      case 7:
        key = 'sentence_special_two'
        break
      case 9:
        key = 'sentence_business_trip'
        break
      default:
        key = 'sentence_custom_time'
    }
    return t('phrases.' + key, employeeCount)
  }

  getSentence(employeeCount: number): string {
    const { t } = i18n

    if (this.nameIsCustom) {
      return (
        t('phrases.sentence_custom_time', employeeCount) + ' ' + this.name + '.'
      )
    }
    return Task.getSentence(this.task_id, employeeCount)
  }
  getTechnicalNameFinish(): string {
    const { t } = i18n
    if (this.nameIsCustom) {
      return this.name + ' ' + t('words.end')
    }
    return Task.getTechnicalNameFinish(this.task_id)
  }

  static getIcon(id: number): string {
    switch (id) {
      case 10:
        return 'fa-clock'
      case 5:
        return 'fa-mug-hot'
      case 2:
        return 'fa-truck-ramp-box'
      case 3:
        return 'fa-truck-fast'
      case 9:
        return 'fa-briefcase'
      case 4:
        return 'fa-rectangle-history'
      case 8:
        return 'fa-right-from-bracket'
      case 6:
        return 'fa-star'
      case 7:
        return 'fa-star'
      case 11:
        return 'fa-circle-exclamation'
      case 12:
        return 'fa-circle-exclamation'
      case 13:
        return 'fa-circle-exclamation'
      default:
        return 'fa-circle-question'
    }
  }

  getIcon(): string {
    return Task.getIcon(this.task_id)
  }

  static getColor(id: number): string {
    switch (id) {
      case 10:
        return '#34D399'
      case 5:
        return '#F87171'
      case 2:
        return '#FCD34D'
      case 3:
        return '#F59E0B'
      case 9:
        return '#99C749'
      case 4:
        return '#60A5FA'
      case 6:
        return '#A78BFA'
      case 7:
        return '#A78BFA'
      case 8:
        return ''
      case 11:
        return '#94a3b8'
      case 12:
        return '#94a3b8'
      case 13:
        return '#94a3b8'
      default:
        return '#A78BFA'
    }
  }

  getColor() {
    return Task.getColor(this.task_id)
  }

  static getActiveColor(id: number): string {
    const base = Task.getColor(id)
    return colorsea(base).darken(12).hex()
  }

  getActiveColor() {
    return Task.getActiveColor(this.task_id)
  }

  static id = {
    CLOCKIN: 10,
    CLOCKOUT: 8,
    WORK: 10,
    BREAK: 5,
    PROJECT: 4,
    DRIVE: 3,
    LOAD: 2,
    DUTY: 9,
    SPECIAL1: 6,
    SPECIAL2: 7,
    _NOT_RATED: 11,
    _LOGOUT_DURING_CORE_TIME: 12,
    _ABSENT_DURING_CORE_TIME: 13,
  }

  static serverTaskIds = [
    Task.id._NOT_RATED,
    Task.id._LOGOUT_DURING_CORE_TIME,
    Task.id._ABSENT_DURING_CORE_TIME,
  ]

  static finishableTaskIds = [
    Task.id.BREAK, // go back to last workTask
    Task.id.DRIVE, // go back to last MainWorkTask
    Task.id.LOAD,
    Task.id.DUTY,
    Task.id.SPECIAL1,
    Task.id.SPECIAL2,
  ]

  static taskIsWork(id: number): boolean {
    const notWorkTaskIds = [
      Task.id.CLOCKOUT,
      Task.id.BREAK,
      Task.id._NOT_RATED,
      Task.id._LOGOUT_DURING_CORE_TIME,
      Task.id._ABSENT_DURING_CORE_TIME,
    ]
    if (notWorkTaskIds.includes(id)) return false
    return true
  }

  static taskCanBeFollowedByClockout(id: number): boolean {
    const notFollowedByClockout = [
      Task.id.CLOCKOUT,
      Task.id._LOGOUT_DURING_CORE_TIME,
      Task.id._ABSENT_DURING_CORE_TIME,
    ]
    if (notFollowedByClockout.includes(id)) return false
    return true
  }

  static taskIsNotNormalWork(id: number): boolean {
    return id !== 10 && id !== 8
  }

  static isWorkplanTask(id: number): boolean {
    const workplanTasks = [
      Task.id._ABSENT_DURING_CORE_TIME,
      Task.id._LOGOUT_DURING_CORE_TIME,
      Task.id._NOT_RATED,
    ]
    return workplanTasks.includes(id)
  }
}

type TTaskId = (typeof Task.id)[keyof typeof Task.id]
export { Task, type TTaskId }
